export interface StationTrack {
  id: string;
  title: string;
  audioUrl: string;
  durationSeconds: number;
  speaker?: string;
  imageUrl?: string;
  /** The chain block this track belongs to, for "now playing — block" display. */
  playlistId?: string;
  playlistName?: string;
}

export interface StationPosition {
  index: number;
  offsetSeconds: number;
}

export interface NowPlaying {
  available: boolean;
  serverTime: number;
  /** Fixed station epoch (ms) the continuous loop is anchored to. */
  anchorMs: number;
  totalDuration: number;
  currentIndex: number;
  offsetSeconds: number;
  playlist: StationTrack[];
}

export function stationTrackFromJson(json: Record<string, unknown>): StationTrack {
  return {
    id: json.id as string,
    title: (json.title as string | undefined) ?? "Untitled",
    audioUrl: (json.audio_url as string | undefined) ?? "",
    durationSeconds: (json.duration_seconds as number | undefined) ?? 0,
    speaker: json.speaker as string | undefined,
    imageUrl: json.image_url as string | undefined,
    playlistId: json.playlist_id as string | undefined,
    playlistName: json.playlist_name as string | undefined,
  };
}

export function nowPlayingFromJson(json: Record<string, unknown>): NowPlaying {
  const rawPlaylist = (json.playlist as unknown[] | undefined) ?? [];
  return {
    available: (json.available as boolean | undefined) ?? false,
    serverTime: (json.serverTime as number | undefined) ?? 0,
    anchorMs: (json.anchorMs as number | undefined) ?? 0,
    totalDuration: (json.totalDuration as number | undefined) ?? 0,
    currentIndex: (json.currentIndex as number | undefined) ?? 0,
    offsetSeconds: (json.offsetSeconds as number | undefined) ?? 0,
    playlist: rawPlaylist.map((entry) => stationTrackFromJson(entry as Record<string, unknown>)),
  };
}

export function nowPlayingEmpty(): NowPlaying {
  return {
    available: false,
    serverTime: 0,
    anchorMs: 0,
    totalDuration: 0,
    currentIndex: 0,
    offsetSeconds: 0,
    playlist: [],
  };
}

export function currentTrack(nowPlaying: NowPlaying): StationTrack | null {
  if (
    !nowPlaying.available ||
    nowPlaying.currentIndex < 0 ||
    nowPlaying.currentIndex >= nowPlaying.playlist.length
  ) {
    return null;
  }
  return nowPlaying.playlist[nowPlaying.currentIndex] ?? null;
}

export interface PlaylistSummary {
  id: string;
  name: string;
  itemCount: number;
  totalDuration: number;
}

export function playlistSummaryFromJson(json: Record<string, unknown>): PlaylistSummary {
  return {
    id: json.id as string,
    name: (json.name as string | undefined) ?? "Untitled",
    itemCount: (json.item_count as number | undefined) ?? 0,
    totalDuration: (json.total_duration as number | undefined) ?? 0,
  };
}

export interface ChainEntry {
  position: number;
  playlistId: string;
  playlistName: string;
}

export function chainEntryFromJson(json: Record<string, unknown>): ChainEntry {
  return {
    position: (json.position as number | undefined) ?? 0,
    playlistId: json.playlist_id as string,
    playlistName: (json.playlist_name as string | undefined) ?? "Untitled",
  };
}

export function resolveNowPlayingAt(nowPlaying: NowPlaying, serverNowMs: number): StationPosition {
  if (!nowPlaying.available || nowPlaying.totalDuration <= 0 || nowPlaying.playlist.length === 0) {
    return { index: 0, offsetSeconds: 0 };
  }

  const elapsed = (serverNowMs - nowPlaying.anchorMs) / 1000;
  const clamped = elapsed < 0 ? 0 : elapsed;
  const posInCycle = clamped % nowPlaying.totalDuration;

  let acc = 0;
  for (let i = 0; i < nowPlaying.playlist.length; i++) {
    const duration = nowPlaying.playlist[i]?.durationSeconds ?? 0;
    if (posInCycle < acc + duration) {
      return { index: i, offsetSeconds: posInCycle - acc };
    }
    acc += duration;
  }

  return { index: nowPlaying.playlist.length - 1, offsetSeconds: 0 };
}
